import { motion } from 'framer-motion';
import { ArrowRight, BookOpen, Hexagon, TrendingUp, Users, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

// Google Icon SVG Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Home: React.FC = () => {
  const { user, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [isSigningIn, setIsSigningIn] = useState(false);

  const features = [
    {
      icon: BookOpen,
      title: 'Interactive Exercises',
      description:
        'Step-by-step exercises pulled from GitHub repositories with real-time progress tracking.',
    },
    {
      icon: Users,
      title: 'Student Portal',
      description:
        'Personalized dashboard showing your learning journey and recommended next steps.',
    },
    {
      icon: TrendingUp,
      title: 'Learning Paths',
      description:
        'AI-powered recommendations for optimal learning progression and skill development.',
    },
  ];

  const stats = [
    { number: '100+', label: 'Exercises Available' },
    { number: '50+', label: 'Learning Paths' },
    { number: '24/7', label: 'Access' },
  ];

  return (
    <div className='relative overflow-hidden'>
      {/* Background Hexagons */}
      <div className='absolute inset-0 -z-10'>
        <div className='absolute top-20 left-10 w-32 h-32 opacity-10'>
          <div className='hexagon bg-hexagon animate-hexagon-float'></div>
        </div>
        <div className='absolute top-40 right-20 w-24 h-24 opacity-10'>
          <div className='hexagon bg-accent-500 animate-hexagon-spin'></div>
        </div>
        <div className='absolute bottom-20 left-1/4 w-20 h-20 opacity-10'>
          <div className='hexagon bg-hexagon-dark animate-hexagon-pulse'></div>
        </div>
      </div>

      {/* Hero Section */}
      <section className='relative py-20 lg:py-32'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className='mb-8'
          >
            <div className='w-24 h-24 mx-auto mb-6'>
              <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
                <Hexagon className='w-12 h-12 text-white' />
              </div>
            </div>
            <h1 className='text-4xl lg:text-6xl font-bold text-gray-900 mb-6'>
              Welcome to <span className='gradient-text'>LuxLabz</span>
            </h1>
            <p className='text-xl lg:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto'>
              Your gateway to interactive learning through carefully curated
              exercises and personalized learning paths.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className='flex flex-col sm:flex-row gap-4 justify-center'
          >
            {user ? (
              <Link
                to='/dashboard'
                className='hexagon-button inline-flex items-center space-x-2'
              >
                <span>Go to Dashboard</span>
                <ArrowRight className='w-5 h-5' />
              </Link>
            ) : (
              <>
                <button
                  onClick={async () => {
                    setIsSigningIn(true);
                    try {
                      await signInWithGoogle();
                      // Navigation will happen via AuthCallback after OAuth redirect
                    } catch (error) {
                      console.error('Sign in error:', error);
                      setIsSigningIn(false);
                      navigate('/login');
                    }
                  }}
                  disabled={isSigningIn}
                  className='hexagon-button inline-flex items-center justify-center space-x-2 disabled:opacity-50'
                >
                  {isSigningIn ? (
                    <>
                      <Loader2 className='w-5 h-5 animate-spin' />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <GoogleIcon />
                      <span>Sign in with Google</span>
                      <ArrowRight className='w-5 h-5' />
                    </>
                  )}
                </button>
                <Link
                  to='/login'
                  className='px-8 py-3 border-2 border-hexagon text-hexagon rounded-lg hover:bg-hexagon hover:text-white transition-all duration-300 inline-flex items-center justify-center'
                >
                  Other Options
                </Link>
              </>
            )}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className='py-20 bg-white/50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className='text-center mb-16'
          >
            <h2 className='text-3xl lg:text-4xl font-bold text-gray-900 mb-4'>
              Why Choose LuxLabz?
            </h2>
            <p className='text-xl text-gray-600 max-w-2xl mx-auto'>
              Experience learning reimagined with our cutting-edge platform
              designed for modern students.
            </p>
          </motion.div>

          <div className='grid md:grid-cols-3 gap-8'>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className='text-center group'
                >
                  <div className='w-20 h-20 mx-auto mb-6'>
                    <div className='hexagon bg-gradient-to-r from-hexagon to-hexagon-dark flex items-center justify-center group-hover:scale-110 transition-transform duration-300'>
                      <Icon className='w-10 h-10 text-white' />
                    </div>
                  </div>
                  <h3 className='text-xl font-semibold text-gray-900 mb-3'>
                    {feature.title}
                  </h3>
                  <p className='text-gray-600'>{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className='py-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid md:grid-cols-3 gap-8'>
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className='text-center'
              >
                <div className='text-4xl lg:text-5xl font-bold gradient-text mb-2'>
                  {stat.number}
                </div>
                <div className='text-gray-600 text-lg'>{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='py-20 bg-gradient-to-r from-hexagon to-accent-500'>
        <div className='max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className='text-3xl lg:text-4xl font-bold text-white mb-6'>
              Ready to Start Your Learning Journey?
            </h2>
            <p className='text-xl text-white/90 mb-8'>
              Join thousands of students who are already advancing their skills
              with LuxLabz.
            </p>
            {!user && (
              <button
                onClick={async () => {
                  setIsSigningIn(true);
                  try {
                    await signInWithGoogle();
                    // Navigation will happen via AuthCallback after OAuth redirect
                  } catch (error) {
                    console.error('Sign in error:', error);
                    setIsSigningIn(false);
                    navigate('/login');
                  }
                }}
                disabled={isSigningIn}
                className='inline-flex items-center justify-center space-x-2 bg-white text-hexagon px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50'
              >
                {isSigningIn ? (
                  <>
                    <Loader2 className='w-5 h-5 animate-spin' />
                    <span>Signing in...</span>
                  </>
                ) : (
                  <>
                    <GoogleIcon />
                    <span>Sign in with Google</span>
                    <ArrowRight className='w-5 h-5' />
                  </>
                )}
              </button>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
